// BookingSystem API Client for Frontend Integration
// Production-ready TypeScript API client for Frankenfloss Booking System

export interface AuthResponse {
  success: boolean;
  token?: string;
  refreshToken?: string;
  user?: UserDto;
  message?: string;
  errors?: string[];
  requiresEmailVerification?: boolean;
  errorCode?: string; // For specific error codes like 'EMAIL_VERIFICATION_REQUIRED'
}

export interface UserDto {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  customerType: 'Individual' | 'Business';
  role?: 'Customer' | 'Staff' | 'Admin' | 'Support' | 'Accounting' | number;  // Backend sends number: 1=Customer, 2=Staff, 3=Admin, 4=Support, 5=Accounting
  Role?: 'Customer' | 'Staff' | 'Admin' | 'Support' | 'Accounting' | number;  // Backend uses capital R
  isEmailVerified: boolean;
  permissions?: string[];
}

export interface CreateBookingRequest {
  scheduleId: number;
  participantCount: number;
  variant: 'Standard' | 'Premium';
  paymentProvider: 'Stripe' | 'PayPal' | 'SEPA';
  paymentType: 'DownPayment' | 'FullPayment';
  customerInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    companyName?: string;
  };
  sepaDetails?: {
    accountHolder: string;
    iban: string;
    bic: string;
  };
}

export interface BookingResponse {
  id: number;
  bookingNumber: string;
  status: 'Pending' | 'Confirmed' | 'Cancelled';
  totalPrice: number;
  createdAt: string;
  schedule: {
    date: string;
    time: string;
    description: string;
  };
}

export interface PaymentResponse {
  success: boolean;
  paymentId: string;
  paymentUrl?: string;
  message: string;
}

export interface AvailabilityResponse {
  isAvailable: boolean;
  availableSpots: number;
  message: string;
  requiresAdminApproval?: boolean;
}

export interface PricingResponse {
  totalAmount: number;
  downPaymentAmount: number;
  serviceAmount: number;
  foodAmount: number;
  vatDetails: {
    serviceVAT: number;
    foodVAT: number;
    totalVAT: number;
  };
  discountApplied?: {
    code: string;
    amount: number;
    percentage: number;
  };
}

export interface BookingOption {
  description: string;
  pricePerPerson: number;
  totalPrice: number;
  originalTotalPrice: number;
  savingsAmount: number;
  tierDescription: string;
  refundAmount?: number;
  netAmount?: number;
}

export interface BookingOptionsResponse {
  hasExistingBookings: boolean;
  existingParticipants: number;
  newParticipants: number;
  totalParticipants: number;
  existingBookingIds: number[];
  optionA?: BookingOption;
  optionB?: BookingOption;
}

export interface PriceCalculationResponse {
  tourTypeId: number;
  tourTypeName: string;
  participants: number;
  pricePerPerson: number;
  totalPrice: number;
  tierDescription: string;
  tierMinParticipants?: number;
  tierMaxParticipants?: number;
}

// ===== SELF-SERVICE BOOKING INTERFACES =====

export interface SelfServiceCurrentAddOn {
  id: number;
  addOnServiceId: number;
  name: string;
  quantity: number;
  quantityFree: number;
  unitPrice: number;
  taxRate: number;
  isAccommodation: boolean;
  isOptionalChoice: boolean;
  bedsPerUnit?: number;
  pricingType?: string; // PerPerson, PerGroup, Fixed, etc.
  isIncludedInTourPrice: boolean;
  quantityMultiplier: number;
}

export interface SelfServiceAvailableAddOn {
  addOnServiceId: number;
  name: string;
  unitPrice: number;
  taxRate: number;
  isAccommodation: boolean;
  bedsPerUnit?: number;
  isOptionalChoice: boolean;
  categoryId?: number;
  isIncludedInTourPrice: boolean;
  quantityMultiplier: number;
}

export interface SelfServiceAvailability {
  isAvailable: boolean;
  tripDate: string;
  participantChangesAllowed: boolean;
  participantDeadline?: string;
  addOnChangesAllowed: boolean;
  addOnDeadline?: string;
  availableCapacity: number;
  unavailableReason?: string;
  currentAddOns: SelfServiceCurrentAddOn[];
  availableAddOns: SelfServiceAvailableAddOn[];
  tourTypeCode?: string;
  voucherCode?: string;
  voucherDiscount: number;
}

export interface SelfServiceAddOnChange {
  addOnId: number;
  newQuantity: number;
}

export interface SelfServiceUpdateRequest {
  adultCount?: number;
  childCount?: number;
  infantCount?: number;
  freeParticipantWithSeatCount?: number;
  freeParticipantNoSeatCount?: number;
  addOnChanges?: SelfServiceAddOnChange[];
  voucherCode?: string;
}

export interface SelfServicePriceChange {
  description: string;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
}

export interface SelfServiceChangesSummary {
  participantChanges: string[];
  addOnChanges: string[];
  priceChanges: SelfServicePriceChange[];
}

export interface SelfServiceResult {
  success: boolean;
  errorMessage?: string;
  bookingId: number;
  bookingNumber: string;
  originalTotal: number;
  newTotal: number;
  additionalAmount: number;
  voucherRedemptionAmount?: number;
  additionalInvoiceId?: number;
  additionalInvoiceNumber?: string;
  changesSummary?: SelfServiceChangesSummary;
}

export interface SelfServiceValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  preview?: SelfServiceResult;
}

export class BookingSystemAPI {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL?: string) {
    // Use environment variable from central config (API_BASE_URL includes /api)
    const API_BASE_URL = (typeof window !== 'undefined' && (window as any).__REACT_APP_API_URL__) ||
                         process.env.REACT_APP_API_URL ||
                         'http://localhost:5174/api';
    this.baseURL = baseURL || API_BASE_URL;
    console.log('BookingSystemAPI initialized with baseURL:', this.baseURL);
    this.token = localStorage.getItem('authToken');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('authToken', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('authToken');
  }

  private async request<T>(
    method: string,
    endpoint: string,
    data?: any
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method,
        headers,
        body: data ? JSON.stringify(data) : undefined,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || `HTTP ${response.status} ${response.statusText}`;
        
        console.error(`API Error: ${method} ${endpoint}`, {
          status: response.status,
          statusText: response.statusText,
          errorData
        });
        
        // HOTFIX: Spezifische HTTP Status Codes
        if (response.status === 405) {
          throw new Error(`HTTP 405 Method Not Allowed: ${method} ${endpoint}`);
        } else if (response.status === 401) {
          throw new Error(`HTTP 401 Unauthorized: ${endpoint}`);
        } else if (response.status === 403) {
          throw new Error(`HTTP 403 Forbidden: ${endpoint}`);
        } else if (response.status === 422) {
          throw new Error(errorMessage); // Keep original message for 422
        }
        
        throw new Error(errorMessage);
      }

      return response.json();
    } catch (error) {
      console.error(`Network Error: ${method} ${endpoint}`, error);
      throw error;
    }
  }

  // Authentication
  async login(email: string, password: string): Promise<AuthResponse> {
    console.log('API login called with email:', email);
    
    try {
      const response = await this.request<any>(
        'POST',
        '/auth/login',
        { email, password }
      );
      console.log('Raw API response:', response);

      // Handle both direct response and nested response structure
      if (response.success && (response.token || response.accessToken)) {
        // Direct response structure (current mock backend)
        const token = response.token || response.accessToken;
        
        if (token) {
          this.setToken(token);
        }
        
        // Return the user as-is (role will be extracted from JWT in AuthContext)
        const result = {
          success: true,
          token: token,
          refreshToken: response.refreshToken,
          user: response.user,
          message: response.message,
          errors: response.errors || [],
          requiresEmailVerification: response.requiresEmailVerification || false
        };
        console.log('Transformed auth response:', result);
        return result;
      }
      
      // Also check for nested data structure (future backend compatibility)
      if (response.success && response.data) {
        const authData = response.data;
        console.log('Auth data extracted from nested structure:', authData);
        
        if (authData.accessToken) {
          this.setToken(authData.accessToken);
        }
        
        return {
          success: true,
          token: authData.accessToken,
          refreshToken: authData.refreshToken,
          user: authData.user,
          message: authData.message,
          errors: authData.errors || [],
          requiresEmailVerification: authData.requiresEmailVerification || false
        };
      }

      console.log('Login failed, returning error response');
      return {
        success: false,
        message: response.message || 'Login fehlgeschlagen',
        errors: response.errors || ['Unbekannter Fehler']
      };
    } catch (error: any) {
      console.error('Login error caught:', error);
      
      // Convert error to response format
      return {
        success: false,
        message: error.message || 'Anmeldung fehlgeschlagen',
        errors: [error.message || 'Ein Fehler ist aufgetreten'],
        requiresEmailVerification: error.message?.includes('E-Mail-Adresse muss vor der Anmeldung verifiziert werden')
      };
    }
  }

  async register(userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    acceptedTerms: boolean;
    acceptedPrivacyPolicy: boolean;
    companyName?: string;
  }): Promise<AuthResponse> {
    // Backend expects confirmPassword and acceptPrivacyPolicy (not acceptedPrivacyPolicy)
    const requestData = {
      email: userData.email,
      password: userData.password,
      confirmPassword: userData.password, // Use same password for confirmation
      firstName: userData.firstName,
      lastName: userData.lastName,
      companyName: userData.companyName,
      acceptTerms: userData.acceptedTerms,
      acceptPrivacyPolicy: userData.acceptedPrivacyPolicy
    };
    
    const response = await this.request<any>('POST', '/auth/register', requestData);
    
    // Handle nested response structure from backend
    if (response.success && response.data) {
      return {
        success: true,
        message: response.data.message || response.message,
        errors: response.data.errors || [],
        token: response.data.accessToken || '',
        refreshToken: response.data.refreshToken || '',
        user: response.data.user,
        requiresEmailVerification: response.data.requiresEmailVerification || false
      };
    }
    
    return response;
  }

  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem('refreshToken');
    return this.request<AuthResponse>('POST', '/auth/refresh-token', {
      refreshToken
    });
  }

  async verifyEmail(token: string): Promise<{
    success: boolean;
    message: string;
    alreadyVerified?: boolean;
  }> {
    try {
      const response = await this.request<any>(
        'GET',
        `/auth/verify-email?token=${encodeURIComponent(token)}`
      );

      return {
        success: response.success || false,
        message: response.message || 'E-Mail-Verifizierung fehlgeschlagen',
        alreadyVerified: response.data?.alreadyVerified || false,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'E-Mail-Verifizierung fehlgeschlagen',
        alreadyVerified: false,
      };
    }
  }

  async resendVerificationEmail(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.request<any>(
        'POST',
        '/auth/resend-verification',
        { email }
      );

      return {
        success: response.success || false,
        message: response.message || 'Fehler beim Versenden der Email'
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.message || 'Fehler beim Versenden der Email'
      };
    }
  }

  async logout(): Promise<void> {
    // HOTFIX: Mock-Backend has no logout endpoint
    // Just clear the local token without API call
    console.log('Logging out - clearing local auth token');
    this.clearToken();
    
    // Optional: In production, you would make an API call here
    // try {
    //   await this.request('POST', '/auth/logout');
    // } finally {
    //   this.clearToken();
    // }
  }

  // Availability
  async checkAvailability(
    scheduleId: number,
    participants: number
  ): Promise<AvailabilityResponse> {
    return this.request<AvailabilityResponse>(
      'GET',
      `/availability/${scheduleId}?participants=${participants}`
    );
  }

  async getSchedules(startDate?: string, endDate?: string): Promise<any[]> {
    const params = new URLSearchParams();
    
    if (startDate) {
      params.append('startDate', startDate);
    }
    
    if (endDate) {
      params.append('endDate', endDate);
    }
    
    const queryString = params.toString();
    const endpoint = queryString ? `/availability/schedules?${queryString}` : '/availability/schedules';
    
    return this.request<any[]>('GET', endpoint);
  }

  // Pricing - HOTFIX: Korrekte Datenstruktur und Fehlerbehandlung
  async calculatePricing(params: {
    participants: number;
    selectedDate: string;
    variant: string;
    extras?: any[];
  }): Promise<PricingResponse> {
    
    // HOTFIX: Variant Mapping und Validation
    const variantMapping: Record<string, string> = {
      'Standard': 'Standard',
      'AllInPremium': 'AllInPremium', 
      'All In Premium': 'AllInPremium',
      'Kleingruppe': 'Standard',
      'standard': 'Standard',
      'allinpremium': 'AllInPremium',
      'all_in_premium': 'AllInPremium'
    };

    // HOTFIX: Sichere Variant-Konvertierung
    const normalizedVariant = variantMapping[params.variant] || 'Standard';
    
    // HOTFIX: Spezialbehandlung für Kleingruppen
    const isSmallGroup = params.participants < 30;
    const finalVariant = isSmallGroup ? 'Standard' : normalizedVariant;

    // HOTFIX: Request-Validierung VOR API-Aufruf
    if (isSmallGroup && !['Standard'].includes(finalVariant)) {
      throw new Error('Für Gruppen unter 30 Personen ist nur Standard-Buchung verfügbar');
    }

    const requestData = {
      participants: params.participants,
      selectedDate: params.selectedDate,
      variant: finalVariant,
      extras: params.extras || [],
      isSmallGroup
    };

    console.log('HOTFIX: Sending pricing request:', requestData);

    try {
      return await this.request<PricingResponse>('POST', '/pricing/calculate', requestData);
    } catch (error: any) {
      // HOTFIX: Detaillierte Fehlerbehandlung
      if (error.message?.includes('Mindestteilnehmerzahl')) {
        throw new Error('Für diese Gruppengröße ist eine Anfrage erforderlich');
      }
      if (error.message?.includes('Buchungsvariante')) {
        throw new Error('Ungültige Buchungsvariante. Verfügbare Optionen: Standard, AllInPremium');
      }
      throw error;
    }
  }

  // HOTFIX: Backward compatibility - Legacy-Methode
  async calculatePricingLegacy(
    scheduleId: number,
    participantCount: number,
    variant: string,
    discountCode?: string
  ): Promise<PricingResponse> {
    return this.calculatePricing({
      participants: participantCount,
      selectedDate: new Date().toISOString().split('T')[0],
      variant: variant,
      extras: []
    });
  }

  // Bookings
  async createBooking(bookingData: CreateBookingRequest): Promise<BookingResponse> {
    return this.request<BookingResponse>('POST', '/bookings', bookingData);
  }

  async getBookings(): Promise<BookingResponse[]> {
    return this.request<BookingResponse[]>('GET', '/bookings');
  }

  async getBooking(id: number): Promise<BookingResponse> {
    return this.request<BookingResponse>('GET', `/bookings/${id}`);
  }

  async cancelBooking(id: number, reason?: string): Promise<void> {
    return this.request('DELETE', `/bookings/${id}`, { reason });
  }

  // Booking Options (Hybrid Approach for Tiered Pricing)
  async checkBookingOptions(
    userId: number,
    scheduleId: number,
    newParticipants: number,
    variant: string = 'Standard'
  ): Promise<BookingOptionsResponse> {
    return this.request<BookingOptionsResponse>(
      'POST',
      '/booking-options/check',
      {
        userId,
        scheduleId,
        newParticipants,
        variant
      }
    );
  }

  // Calculate price based on tier pricing
  async calculateTieredPrice(
    tourTypeId: number,
    participants: number
  ): Promise<PriceCalculationResponse> {
    return this.request<PriceCalculationResponse>(
      'GET',
      `/tour-types/${tourTypeId}/calculate-price?participants=${participants}`
    );
  }

  // Payment
  async initiatePayment(paymentData: {
    bookingId: number;
    paymentProvider: string;
    amount: number;
    paymentType: string;
    returnUrl: string;
    cancelUrl: string;
    sepaDetails?: any;
  }): Promise<PaymentResponse> {
    return this.request<PaymentResponse>('POST', '/payment/initiate', paymentData);
  }

  async getPaymentStatus(paymentId: string): Promise<any> {
    return this.request('GET', `/payment/status/${paymentId}`);
  }

  // Admin-specific methods - HOTFIX für HTTP 405
  async getAdminSchedules(): Promise<any[]> {
    try {
      console.log('HOTFIX: Attempting admin schedules API call');
      const response = await this.request<any>('GET', '/admin/schedules');
      
      // HOTFIX: Sichere Array-Behandlung
      if (response && response.schedules && Array.isArray(response.schedules)) {
        console.log('HOTFIX: Admin schedules array found:', response.schedules.length);
        return response.schedules;
      } else if (Array.isArray(response)) {
        console.log('HOTFIX: Direct array response:', response.length);
        return response;
      } else {
        console.warn('HOTFIX: Admin schedules response is not an array:', response);
        return [];
      }
    } catch (error: any) {
      console.error('HOTFIX: Admin schedules API failed:', error);
      
      // HOTFIX: Bei 401/403/405 Fehlern - klarer Fallback
      if (error.message?.includes('401') || error.message?.includes('403') || error.message?.includes('405')) {
        console.warn('HOTFIX: Auth/Method error for admin API, will use fallback');
        throw new Error('Admin API nicht verfügbar - Auth/Method Error');
      }
      
      // HOTFIX: Bei anderen Fehlern - Fallback auf normale Schedules
      console.warn('HOTFIX: Admin API failed, will try fallback');
      throw error;
    }
  }

  async createAdminBooking(bookingData: {
    date: string;
    startTime: string;
    endTime: string;
    groupName: string;
    participantCount: number;
    contactName: string;
    contactEmail: string;
    contactPhone: string;
    eventType: string;
    isFlexible: boolean;
    variant: string;
    specialRequests: string;
    adminCreated: boolean;
  }): Promise<any> {
    return this.request('POST', '/admin/bookings', bookingData);
  }

  async getUserProfile(): Promise<UserDto> {
    console.log('getUserProfile called');
    const response = await this.request<any>('GET', '/user/profile');
    console.log('getUserProfile raw response:', response);
    
    // Handle different response structures
    if (response.data) {
      return response.data;
    }
    
    return response;
  }

  async updateUserProfile(userData: Partial<UserDto>): Promise<UserDto> {
    return this.request<UserDto>('PUT', '/user/profile', userData);
  }

  async getUserAddresses(): Promise<any[]> {
    return this.request<any[]>('GET', '/user/addresses');
  }

  async addUserAddress(address: any): Promise<any> {
    return this.request('POST', '/user/addresses', address);
  }

  // GDPR
  async exportPersonalData(): Promise<any> {
    return this.request('GET', '/gdpr/export-data');
  }

  async requestDataDeletion(reason: string): Promise<any> {
    return this.request('POST', '/gdpr/delete-request', {
      reason,
      confirmDeletion: true
    });
  }

  // ===== SELF-SERVICE BOOKING METHODS =====

  /**
   * Check if self-service updates are available for a booking
   * Returns deadline information and available capacity
   */
  async getSelfServiceAvailability(bookingId: number): Promise<SelfServiceAvailability> {
    return this.request<SelfServiceAvailability>(
      'GET',
      `/bookings/${bookingId}/self-service-availability`
    );
  }

  /**
   * Validate a self-service update without committing
   * Returns preview of price changes and validation errors
   */
  async validateSelfServiceUpdate(
    bookingId: number,
    request: SelfServiceUpdateRequest
  ): Promise<SelfServiceValidationResult> {
    return this.request<SelfServiceValidationResult>(
      'POST',
      `/bookings/${bookingId}/self-service-validate`,
      request
    );
  }

  /**
   * Process a self-service update (increase participants/add-ons)
   * Creates additional invoice if price difference exists
   */
  async processSelfServiceUpdate(
    bookingId: number,
    request: SelfServiceUpdateRequest
  ): Promise<SelfServiceResult> {
    return this.request<SelfServiceResult>(
      'POST',
      `/bookings/${bookingId}/self-service-update`,
      request
    );
  }
}

// German Business Rules Helper
export class GermanBusinessRules {
  static validateMinimumParticipants(count: number, isAdminBooking = false): boolean {
    if (isAdminBooking) return true;
    return count >= 30;
  }

  static validateWeekendBooking(date: Date, variant: string): boolean {
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    if (isWeekend) {
      return variant === 'Premium';
    }
    return true;
  }

  static requiresFullPayment(tripDate: Date): boolean {
    const weeksUntilTrip = (tripDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 7);
    return weeksUntilTrip < 8;
  }

  static calculateGermanVAT(serviceAmount: number, foodAmount: number) {
    const serviceVAT = serviceAmount * 0.19; // 19% for services
    const foodVAT = foodAmount * 0.07;       // 7% for food
    return {
      serviceVAT,
      foodVAT,
      totalVAT: serviceVAT + foodVAT,
      netAmount: serviceAmount + foodAmount,
      totalAmount: serviceAmount + foodAmount + serviceVAT + foodVAT
    };
  }

  static formatGermanCurrency(amount: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  }

  static formatGermanDate(date: Date): string {
    return new Intl.DateTimeFormat('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  }
}

export default BookingSystemAPI;